import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'

import { DrawerBodyWrapper, DrawerFooterWrapper, TOAST_TYPE } from '@base'
import { Button as ButtonV2 } from '@baseV2'
import { enquiryInputType, strings } from '@constants'
import { getProductByCategory, saveAllBrands } from '@data'
import { showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import EnquiryItemForm from './EnquiryItemForm'
import { getDuplicateGradeRowIds, findDuplicateEnquiryItem } from './EnquiryItemForm/inputAdapters'
import { Devices, Spacings } from '@styles'
import { createNewEnquiryFromData, fetchBrands, updateNewEnquiry } from './CreateEnquiry/state/actions'
import { generateItemId, isBrandOptional } from '@utils'

const ActionButton = styled(ButtonV2)`
	margin-left: ${Spacings.SPACING_4B};
	max-width: 14rem;

	&:last-of-type {
		margin-left: 0;
	}

	flex-grow: 1;

	@media ${Devices.tablet} {
		flex-grow: 0;
	}

	margin: ${({ margin }) => margin};
`

const initialFormState = {
	category: {
		label: strings('category'),
		disabled: false,
		readOnly: false,
		displayKey: 'category_name',
		primaryKey: 'category_id',
		placeholder: strings('select_a_category'),
		data: [],
		value: '',
		dirty: false,
		shouldFetchProducts: { flag: true },
	},
	product: {
		label: strings('product'),
		disabled: false,
		readOnly: false,
		displayKey: 'product_name',
		primaryKey: 'product_id',
		placeholder: strings('search_product'),
		data: [],
		value: '',
		dirty: false,
	},
	grade: {
		label: strings('grade'),
		placeholder: strings('enter', 'grade'),
		readOnly: false,
		value: '',
		dirty: false,
	},
	requiredWeight: {
		label: strings(['weight', '(', 'mt', ')']),
		disabled: false,
		readOnly: false,
		value: '',
		dirty: false,
	},
	targetPrice: {
		label: strings('target', 'price'),
		disabled: false,
		readOnly: false,
		value: '',
		dirty: false,
	},
	brands: {
		label: strings('brands'),
		disabled: false,
		readOnly: false,
		displayKey: 'name',
		primaryKey: 'id',
		placeholder: strings('select_brands'),
		data: [],
		value: [],
		dirty: false,
	},
	message: {
		label: strings('message'),
		disabled: false,
		readOnly: false,
		value: '',
		dirty: false,
	},
	tcRequired: {
		label: strings('tc_reqd'),
		disabled: false,
		value: false,
		dirty: false,
	},
	inputType: {
		label: strings('type'),
		disabled: false,
		value: '',
		dirty: false,
	},
	hsn_code: {
		label: strings('hsn', 'code'),
		placeholder: strings('enter', 'hsn', 'code'),
		disabled: true,
		readOnly: false,
		value: '',
		dirty: false,
	},
	gst: {
		label: strings('gst'),
		placeholder: strings('enter', 'gst'),
		readOnly: true,
		value: '',
		dirty: false,
	},
}

const deriveFormStateFromItemData = (oldState = initialFormState, data, editable = true, selectedbrands = [], categories = []) => {
	const derivedState = { ...oldState }
	const checkEdit = Object.keys(data).length

	derivedState.category.value = categories.find((item) => item.category_id === data?.category_id) || {}
	derivedState.category.disabled = checkEdit || !editable
	derivedState.category.readOnly = !editable

	derivedState.product.value = { ...data }
	derivedState.product.disabled = checkEdit || !editable
	derivedState.product.readOnly = !editable
	derivedState.requiredWeight.value = data?.input_type === enquiryInputType.WEIGHT ? data.required_weight / 1000 : data.required_weight || ''
	derivedState.requiredWeight.disabled = !editable
	derivedState.requiredWeight.readOnly = !editable

	derivedState.inputType.value = data.input_type
	derivedState.inputType.disabled = !editable
	derivedState.inputType.readOnly = !editable

	derivedState.targetPrice.value = data.target_price || ''
	derivedState.targetPrice.disabled = !editable
	derivedState.targetPrice.readOnly = !editable

	derivedState.brands.value = selectedbrands
	derivedState.brands.disabled = !editable
	derivedState.brands.readOnly = !editable

	derivedState.message.value = data.message ?? ''
	derivedState.message.disabled = !editable
	derivedState.message.readOnly = !editable

	derivedState.tcRequired.value = data.tc_required ?? true
	derivedState.tcRequired.disabled = !editable

	derivedState.grade.value = data?.grade || null
	// derivedState.grade.disabled = checkEdit || !editable
	derivedState.grade.readOnly = !editable

	derivedState.hsn_code.value = data?.hsn_code || ''
	derivedState.hsn_code.disabled = true
	derivedState.hsn_code.readOnly = !editable

	derivedState.gst.value = data?.gst
	derivedState.gst.disabled = true

	return derivedState
}

const CreateItem = ({
	itemId = null,
	editable = true,
	getState,
	addNewItemToEnquiryCallback,
	deleteEnquiryItemCallback,
	updateEnquiryItemCallback,
	updateplatformcallback,
	defualtCategoryId = null,
	dispatch,
	chooseEnquiryPhoto,
	activeCommodity,
}) => {
	const globalDispatch = useDispatch()
	const categories = getState().categories
	const brands = getState().brands
	const enquiryItems = getState().newEnquiry.get(['enquiry_items'])
	const checkTmtItem = ['ms_tmt_bars']
	const defaultBrand = { name: 'ANY', id: 'any' }
	const lastCategoryProductsFetched = useRef()
	const [formState, setFormState] = useState(initialFormState)
	// Drives the Product Items skeleton (EnquiryItemForm → SimpleProductTable/TmtProductTable),
	// scoped to just this fetch so the section can show its own loading state.
	const [productsLoading, setProductsLoading] = useState(false)
	const user = useSelector((state) => state.getIn(['user', 'user']))
	// Solar items are not brand-driven, so Brands is not a required field for them.
	const brandOptional = isBrandOptional(user)

	const _enquiryItems = enquiryItems?.find((_item) => _item.get('item_id') === itemId)?.toJS() ?? {}
	// Stable identity so memoized children (EnquiryItemForm → SimpleProductTable) aren't
	// invalidated on every render. Only closes over the stable `setFormState` setter.
	const updateFormState = useCallback((key, updates) => {
		setFormState((_oldState) => ({
			..._oldState,
			[key]: {
				..._oldState[key],
				...updates,
			},
		}))
	}, [])
	// Stable plain-JS snapshot of the immutable enquiry items so the memoized
	// EnquiryItemForm/SimpleProductTable only re-render when the items actually change.
	const enquiryItemsJS = useMemo(() => enquiryItems?.toJS() ?? [], [enquiryItems])
	useEffect(() => {
		updateCategoryState()
		lastCategoryProductsFetched.current = undefined
		let selectedbrands = _enquiryItems?.brands?.length > 0 ? brands?.data?.filter((cls) => _enquiryItems?.brands.includes(cls.name)) : []
		setFormState((_oldState) => deriveFormStateFromItemData(_oldState, _enquiryItems, !!editable, selectedbrands, categories?.data?.data))
	}, [editable])

	const updateCategoryState = () => {
		if (categories?.data?.data) {
			let data = categories?.data?.data
			if (enquiryItems?.toJS().length && enquiryItems?.some((item) => !item.category_id?.includes('scrap'))) {
				data = data.filter((item) => !item?.category_id?.includes('scrap'))
			}
			const updates = { data }
			if (_enquiryItems?.category_id) {
				updates.value = data.find((_item) => _item.category_id === _enquiryItems.category_id)
				updates.shouldFetchProducts = { flag: true }
			}
			updateFormState('category', updates)
		}
	}

	useEffect(() => {
		if (brands?.data) {
			if (!formState.brands.value) {
				updateFormState('brands', { data: brands?.data, value: brands?.data })
			} else {
				updateFormState('brands', { data: brands?.data })
			}
		}
	}, [brands])

	useEffect(() => {
		if (formState.category.dirty) {
			updateFormState('product', { value: '', data: [], tmtProduct: undefined, dirty: false })
		}
		const categoryId = formState.category.value?.category_id
		if (categoryId && categoryId !== lastCategoryProductsFetched.current) {
			lastCategoryProductsFetched.current = categoryId
			setProductsLoading(true)
			getProductByCategory(categoryId)
				.then((data) => {
					const updates = { data, value: '' }
					if (_enquiryItems?.product_id && categoryId === _enquiryItems?.category_id) {
						updates.value = data?.find((_item) => _enquiryItems.product_id === _item.product_id) || formState.product.value
					}
					updateFormState('product', updates)
				})
				.finally(() => {
					setProductsLoading(false)
				})
		}
	}, [formState.category.value, formState.category.shouldFetchProducts])

	useEffect(() => {
		if (defualtCategoryId) {
			const _category = categories?.data?.data?.find((_c) => _c.category_id === defualtCategoryId)
			updateFormState('category', { value: _category, dirty: true })
			updateFormState('hsn_code', { value: _category?.hsn_code, dirty: true })
		}
	}, [defualtCategoryId])

	const formStateToEnquiryItem = () => {
		return {
			category_id: formState.category.value.category_id,
			category_name: formState.category.value.category_name,
			product_id: formState.product.value.product_id,
			product_name: formState.product.value?.product_name,
			required_weight:
				formState.inputType.value === enquiryInputType.WEIGHT
					? parseFloat(formState.requiredWeight.value * 1000)
					: formState.requiredWeight.value,
			input_type: formState.inputType.value,
			target_price: formState.targetPrice.value,
			brands: formState.brands.value.map((a) => a.name),
			message: formState.message.value,
			tc_required: formState.tcRequired.value,
			is_unknown_product: formState.product.value.is_new_item,
			grade: formState?.grade?.value,
			hsn_code: formState?.hsn_code?.value,
			gst: formState.category?.value?.igst,
			item_id: generateItemId(formState?.product?.value?.product_id, formState?.grade?.value),
		}
	}

	const addUpdateHandler = (isUpdate = false) => {
		let _enquiryItemData = formStateToEnquiryItem()

		if (isUpdate) {
			updateHandler(_enquiryItemData)
		} else {
			addNewItemHandler(_enquiryItemData)
		}

		let _brands = formState.brands?.value
			?.filter((_item) => _item.is_new_item === true)
			.map((item) => {
				if (item.is_new_item === true) {
					return { brand_name: item.name, type: 'SECONDARY', commodity: activeCommodity, created_by: user?.user_id }
				}
			})
		if (_brands?.length > 0) {
			saveAllBrands(_brands)
				.then(() => {})
				.catch((error) => {
					console.log(error)
				})
				.finally(() => {
					dispatch(fetchBrands(activeCommodity?.commodity_name))
				})
		}
	}

	const addNewItemHandler = (newEnquiryItemData) => {
		if (newEnquiryItemData?.category_id?.includes('scrap')) {
			updateplatformcallback('RECYCLING')
		}

		if (formState?.product?.tmtProduct === undefined) {
			globalDispatch(showToast(true, strings('msg_wrong_value'), { type: TOAST_TYPE.ERROR }))
			return
		}

		let _tmtItemList = [],
			enqId = getState()?.newEnquiry?.get('enquiry_id'),
			hasDuplicate = false,
			_batchItemIds = new Set()

		formState?.product?.tmtProduct.filter((item) => {
			let _formData = formStateToEnquiryItem()
			if (item?.required_weight !== '') {
				_formData.product_id = item.product_id
				_formData.product_name = item.product_name
				_formData.is_unknown_product = item.is_unknown_product ?? false
				_formData.required_weight =
					formState.inputType.value === enquiryInputType.WEIGHT ? parseFloat(item.required_weight * 1000) : item.required_weight
				_formData.target_price = item.target_price
				_formData.grade = item.grade
				_formData.message = item.message
				_formData.item_id = generateItemId(item?.product_id, item?.grade)
				if (duplicateCheck(_formData)) {
					hasDuplicate = true
					return
				}
				// skip if this product + grade is already queued earlier in this same submission
				if (!_batchItemIds.has(_formData.item_id)) {
					_batchItemIds.add(_formData.item_id)
					if (enqId) {
						_tmtItemList = [..._tmtItemList, _formData]
					} else {
						addNewItemToEnquiryCallback(_formData)
						chooseEnquiryPhoto &&
							dispatch(
								updateNewEnquiry({
									enquiry_photo_id: chooseEnquiryPhoto?.enquiry_photo_id,
								})
							)
					}
				}
			}
		})
		if (enqId && !hasDuplicate) {
			addNewItemToEnquiryCallback(_tmtItemList)
		}
		globalDispatch(toggleDrawer(false))
	}

	const duplicateCheck = (formData) => {
		const duplicateProductGrade = findDuplicateEnquiryItem(formData, enquiryItemsJS)
		if (duplicateProductGrade) {
			globalDispatch(showToast(true, strings('msg_item_already_added', [duplicateProductGrade?.product_name]), { type: TOAST_TYPE.ERROR }))
			return true
		} else {
			return false
		}
	}

	const cancelEditHandler = () => {
		globalDispatch(toggleDrawer(false))
	}

	const updateHandler = (updatedEnquiryItemData) => {
		if (updatedEnquiryItemData.required_weight <= 0) {
			globalDispatch(showToast(true, strings('msg_wrong_value'), { type: TOAST_TYPE.ERROR }))
			return
		}

		if (updatedEnquiryItemData) {
			let _itemId = updatedEnquiryItemData?.item_id
			const duplicate = findDuplicateEnquiryItem(updatedEnquiryItemData, enquiryItemsJS, itemId)
			if (duplicate) {
				globalDispatch(showToast(true, strings('msg_item_already_added', [duplicate?.product_name]), { type: TOAST_TYPE.ERROR }))
				return
			}
			updateEnquiryItemCallback(_itemId, updatedEnquiryItemData, itemId)
			globalDispatch(toggleDrawer(false))
		}
	}

	const deleteButtonHandler = () => {
		deleteEnquiryItemCallback(itemId)
		globalDispatch(toggleDrawer(false))
	}

	const addDisableButton = () => {
		// Brands are optional on the solar commodity, so they only gate the button elsewhere.
		if (!formState.category.value?.category_id) return true
		if (!brandOptional && !formState.brands.value.length) return true
		if (formState?.product?.tmtProduct?.length) {
			let reg = /^[\d]+(?:\.\d{1,3})?$/
			let emptyCheck = formState?.product?.tmtProduct?.some((item) => item?.required_weight)
			if (!emptyCheck) return true
			if (getDuplicateGradeRowIds(formState?.product?.tmtProduct).size) return true
			return formState?.product?.tmtProduct?.find((item) => item?.required_weight && !reg.test(item?.required_weight))
		}
		if (formState?.product?.data.length && !formState?.product?.tmtProduct?.length) return true
		return false
	}

	const updatedisableButton = () => {
		if (checkTmtItem.includes(formState.category.value?.category_id))
			return !formState.category.value?.category_id || (!brandOptional && !formState.brands.value.length)
		if (formState?.product?.value?.is_unknown_product) {
			return true
		}
		return !formState.requiredWeight.value || (!brandOptional && !formState.brands.value.length)
	}

	const renderButtons = () => {
		if (!itemId && editable) {
			return (
				<ActionButton color='primary' disabled={addDisableButton()} onClick={() => addUpdateHandler(false)}>
					{strings('add_item')}
				</ActionButton>
			)
		} else if (itemId && editable) {
			return (
				<>
					<ActionButton disabled={updatedisableButton()} color='primary' onClick={() => addUpdateHandler(true)}>
						{strings('update')}
					</ActionButton>
					<ActionButton variant='outline' color='neutral' onClick={cancelEditHandler}>
						{strings('cancel')}
					</ActionButton>
					<ActionButton margin={`0 auto 0 0`} color='danger' onClick={deleteButtonHandler}>
						{strings('delete')}
					</ActionButton>
				</>
			)
		}
	}

	return (
		<>
			<DrawerBodyWrapper>
				<EnquiryItemForm
					enquiryItems={enquiryItemsJS}
					itemId={itemId}
					state={formState}
					updateState={updateFormState}
					productsLoading={productsLoading}
					defaultBrand={defaultBrand}
					defualtCategoryId={defualtCategoryId}
					checkTmtItem={checkTmtItem}
					activeCommodity={activeCommodity}
				/>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>{renderButtons()}</DrawerFooterWrapper>
		</>
	)
}

export default CreateItem
